import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, Factory, MapPin, Plane, Globe2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { IndustrialEstate } from "@/content/industrial-estates/estates";
import type { CoreServicePillar } from "@/content/service-pillars/core.gen";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { getEstateCoords } from "@/lib/geo-coords";
import { COMPANY } from "@/lib/company";

export const Route = createFileRoute("/industrial-estates/$estate/")({
  loader: async ({
    params,
  }): Promise<{
    estate: IndustrialEstate;
    services: CoreServicePillar[];
  }> => {
    const { ESTATE_CORE_SERVICES, getEstate } = await import(
      "@/content/industrial-estates/estates"
    );
    const estate = getEstate(params.estate);
    if (!estate) throw notFound();
    const { getCoreServicePillar } = await import(
      "@/content/service-pillars/core.gen"
    );
    const services = ESTATE_CORE_SERVICES.map((s) =>
      getCoreServicePillar(s),
    ).filter(Boolean) as CoreServicePillar[];
    return { estate, services };
  },
  head: ({ params, loaderData }) => {
    const estate = loaderData?.estate;
    const site = getActiveSiteUrl();
    if (!estate) {
      return pageHeadForProvince({
        title: "ไม่พบนิคม",
        description: "ไม่พบนิคมนี้",
        path: `/industrial-estates/${params.estate}`,
      });
    }
    const rawTitle = `${estate.nameEn} — B2B Legal Services`;
    const metaTitle = rawTitle.length > 60 ? rawTitle.slice(0, 59) + "…" : rawTitle;
    const rawDesc = `บริการกฎหมาย B2B สำหรับโรงงานใน ${estate.nameTh} (${estate.province}) — Work Permit, Notary, แปลรับรอง, MFA และ Police Clearance รับเคสในนิคม.`;
    const metaDesc = rawDesc.length > 158 ? rawDesc.slice(0, 157) + "…" : rawDesc;
    const base = pageHeadForProvince({
      title: metaTitle,
      description: metaDesc,
      path: `/industrial-estates/${estate.slug}`,
    });
    const coords = getEstateCoords(estate.slug);
    const faqs = [
      {
        q: `ทีม Thai Notary มาที่ ${estate.nameTh} ได้หรือไม่?`,
        a: `มาได้ทุกวันทำการ — ทีมประจำที่ ${estate.province} สามารถเข้ารับเอกสารและพบผู้บริหารต่างชาติที่โรงงานในนิคม ${estate.nameEn} โดยตรง`,
      },
      {
        q: `เอกสารต้องส่ง MFA อย่างไรจาก ${estate.nameTh}?`,
        a: estate.mfaRoute,
      },
      {
        q: `รองรับเอกสารภาษาอะไรบ้างสำหรับนักลงทุนใน ${estate.nameTh}?`,
        a: `รองรับ ${estate.dominantInvestors.join("/")} และอังกฤษ พร้อมรับรองโดยทนาย Notarial Services Attorney และส่งสถานทูตปลายทางในกรุงเทพได้ทันที`,
      },
    ];
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "นิคมอุตสาหกรรม", url: `${site}/industrial-estates` },
              { name: estate.nameTh, url: `${site}/industrial-estates/${estate.slug}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Place",
            "@id": `${site}/industrial-estates/${estate.slug}#place`,
            name: estate.nameEn,
            alternateName: estate.nameTh,
            description: estate.intro,
            address: {
              "@type": "PostalAddress",
              addressLocality: estate.nearestCity,
              addressRegion: estate.province,
              addressCountry: "TH",
            },
            ...(coords
              ? {
                  geo: {
                    "@type": "GeoCoordinates",
                    latitude: coords.lat,
                    longitude: coords.lng,
                  },
                }
              : {}),
            url: `${site}/industrial-estates/${estate.slug}`,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            "@id": `${site}/industrial-estates/${estate.slug}#localbusiness`,
            name: `Thai Notary — ${estate.nameEn}`,
            description: `B2B legal & immigration support for factories inside ${estate.nameEn}, ${estate.province}. On-site visits, MFA legalization, work permits and certified translations.`,
            url: `${site}/industrial-estates/${estate.slug}`,
            telephone: COMPANY.phone,
            priceRange: "฿฿",
            areaServed: [
              { "@type": "Place", name: estate.nameEn },
              { "@type": "AdministrativeArea", name: estate.province },
            ],
            ...(coords
              ? {
                  serviceArea: {
                    "@type": "GeoCircle",
                    geoMidpoint: {
                      "@type": "GeoCoordinates",
                      latitude: coords.lat,
                      longitude: coords.lng,
                    },
                    geoRadius: 10000,
                  },
                }
              : {}),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/industrial-estates/${params.estate}`)) },
      ],
    };
  },
  component: EstatePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">ไม่พบนิคมนี้</h1>
        <Link to="/industrial-estates" className="mt-4 inline-flex items-center gap-1 text-gold">
          <ArrowLeft className="h-4 w-4" /> กลับ
        </Link>
      </div>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 text-center">
      <h1 className="text-xl font-bold">เกิดข้อผิดพลาด</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
});

function EstatePage() {
  const { estate, services } = Route.useLoaderData() as {
    estate: IndustrialEstate;
    services: CoreServicePillar[];
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <nav className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-5xl px-4 py-3 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span>
            <Link to="/industrial-estates" className="hover:text-primary">นิคมอุตสาหกรรม</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">{estate.nameTh}</span>
          </div>
        </nav>

        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <Factory className="h-3.5 w-3.5" />
              {estate.operator} · {estate.province}
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">
              {estate.nameTh} <span className="block text-xl text-muted-foreground md:text-2xl">{estate.nameEn}</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{estate.intro}</p>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-5xl gap-3 px-4 py-8 md:grid-cols-3">
            <Fact icon={MapPin} label="เมืองใกล้สุด" value={estate.nearestCity} />
            <Fact icon={Plane} label="สนามบิน" value={estate.nearestAirport} />
            <Fact icon={Factory} label="จำนวนโรงงาน" value={`${estate.factories}+`} />
            <Fact icon={Globe2} label="นักลงทุนหลัก" value={estate.dominantInvestors.join(", ")} />
            <Fact icon={MapPin} label="สำนักจัดหางาน" value={estate.laborOffice} />
            <Fact icon={MapPin} label="ตม." value={estate.immigrationOffice} />
          </div>
        </section>

        <section className="border-b border-border bg-muted/20">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <h2 className="text-2xl font-bold">บริการ B2B สำหรับ {estate.nameTh}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              เลือกบริการเพื่อดูราคา, ขั้นตอน on-site และ SLA ที่ออกแบบเฉพาะสำหรับโรงงานในนิคมนี้
            </p>
            <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {services.map((sv) => {
                const s = sv.slug;
                return (
                  <Link
                    key={s}
                    to="/industrial-estates/$estate/$service"
                    params={{ estate: estate.slug, service: s }}
                    className="group rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-lg"
                  >
                    <div className="text-xs font-bold uppercase tracking-wide text-gold">
                      {sv.pricing[0]?.price ? `เริ่ม ${sv.pricing[0].price}` : "ตามใบเสนอราคา"}
                    </div>
                    <div className="mt-1 text-base font-extrabold">{sv.h1}</div>
                    <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{sv.metaDesc}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto max-w-4xl px-4 py-10">
            <h2 className="text-xl font-bold">เส้นทาง MFA สำหรับนิคมนี้</h2>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">{estate.mfaRoute}</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Fact({ icon: Icon, label, value }: { icon: typeof Factory; label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-gold" />
        {label}
      </div>
      <div className="mt-1 text-sm font-bold">{value}</div>
    </div>
  );
}